import {
  LEGACY_DROPPED_QUERY_KEYS,
  SEARCH_CRITERIA_QUERY_KEYS,
  fromSearchCriteriaQuery,
  toSearchCriteriaQuery,
  type SearchCriteria,
} from '@/entities/searchCriteria';
import type { QueryObject } from '@/shared/lib/serializeQuery';
import { getUrlQuery, omitQueryKeys, setUrlQuery } from '@/shared/lib/urlQuery';
import type { SearchCriteriaParams } from '@/shared/model';
import { createEmptyFiltersModel } from './filtersModel';
import { fromFiltersQuery, getOwnedFilterQueryKeys, toFiltersQuery } from './filtersUrl';
import { SEARCH_ID_QUERY_KEY, fromSearchIdQuery, toSearchIdQuery } from './searchSession';
import { DEFAULT_SERP_SORT } from '../config/sort';
import type { FiltersModel, SerpSort } from '../model/types';

const SORT_QUERY_KEY = 'sort';

const CRITERIA_QUERY_KEYS = [...SEARCH_CRITERIA_QUERY_KEYS, ...LEGACY_DROPPED_QUERY_KEYS];

const SERP_SORT_TO_URL: Record<SerpSort, string> = {
  popular: 'recommend',
  priceAsc: 'priceUp',
  priceDesc: 'priceDown',
  rating: 'ratingDown',
};

const URL_TO_SERP_SORT: Record<string, SerpSort> = {
  recommend: 'popular',
  priceUp: 'priceAsc',
  priceDown: 'priceDesc',
  ratingDown: 'rating',
  reviewsDown: 'rating',
};

export interface SerpUrlState {
  criteria: SearchCriteria;
  filtersModel: FiltersModel;
  sort: SerpSort;
  searchId: string;
}

export interface SerpUrlParams {
  criteria: Partial<SearchCriteriaParams> | null;
  filtersModel: FiltersModel | null;
  sort: SerpSort | null;
  searchId: string;
}

export function fromSortQuery(query: QueryObject): SerpSort | null {
  const value = typeof query[SORT_QUERY_KEY] === 'string' ? (query[SORT_QUERY_KEY] as string) : '';

  return URL_TO_SERP_SORT[value] ?? null;
}

export function toSerpLinkSortQuery(sort: SerpSort): QueryObject {
  return { [SORT_QUERY_KEY]: SERP_SORT_TO_URL[sort] };
}

export function toSortQuery(sort: SerpSort): QueryObject {
  return sort === DEFAULT_SERP_SORT ? {} : toSerpLinkSortQuery(sort);
}

export function fromSerpUrl(url: string): SerpUrlParams {
  const query = getUrlQuery(url);
  const filters = fromFiltersQuery(query);

  return {
    criteria: fromSearchCriteriaQuery(query),
    filtersModel: filters ? { ...createEmptyFiltersModel(), ...filters } : null,
    sort: fromSortQuery(query),
    searchId: fromSearchIdQuery(query),
  };
}

export function toSerpUrl(url: string, { criteria, filtersModel, sort, searchId }: SerpUrlState): string {
  const query = getUrlQuery(url);
  const preserved = omitQueryKeys(query, [
    ...CRITERIA_QUERY_KEYS,
    ...getOwnedFilterQueryKeys(query),
    SEARCH_ID_QUERY_KEY,
    SORT_QUERY_KEY,
  ]);

  return setUrlQuery(url, {
    ...preserved,
    ...toSearchCriteriaQuery(criteria),
    ...toFiltersQuery(filtersModel),
    ...toSearchIdQuery(searchId),
    ...toSortQuery(sort),
  });
}
